import React from "react";

import BodyText from "Common/BodyText";
import { Ul, Li } from "Common/HTML";

const employeeCostsImage = (
  <img
    alt="A card showing a United Healthcare insurance plan
    and premium with a link saying 'See Costs For All Employees'
    circled."
    src="/images/faq-images/see_employee_costs.png"
  />
);

const planDetailsImage = (
  <img
    alt="A card showing a Regence Blue Shield insurance plan
    and premium with a button saying 'View Plan Details' circled."
    src="/images/faq-images/view_plan_details.png"
  />
);

const answerEligibility = (
  <>
    <BodyText>If you have at least one employee then you are very likely to be eligible to offer benefits for your company.</BodyText>
    <BodyText>When you start an application, we will confirm any plan-specific eligibility requirements for your company.</BodyText>
    <img
      alt="A card showing a United Healthcare insurance plan and
      premium with the word 'Eligible' circled."
      src="/images/faq-images/eligible_plan_card.png"
    />
  </>
);

const answerBudgetNoCalculator = (
  <>
    <BodyText>There are two factors to consider when budgeting: contributions and tax implications.</BodyText>
    <Ul>
      <Li>
        <strong>Contributions:</strong> Your company can choose to contribute between 0% and 100% of the monthly cost for your employees and their dependents.
        Any amount not covered by the company is paid by the employee and is typically deducted from their paychecks.
      </Li>
      <Li>
        <strong>Tax Implications:</strong> Any amount that you contribute to health benefits is tax deductible.
      </Li>
    </Ul>
    <BodyText>
      In this example, the price displayed for the plan is $300/mo per employee. However, when taking contributions and tax implications into account, the true
      cost could be as low as <strong>$120/mo</strong>.
    </BodyText>
    <BodyText>
      $300 * 50% contribution = $150
      <br />
      $150 - 20% tax deduction = $120 (contact your accountant for a precise tax deduction amount)
    </BodyText>
    <BodyText>To see an exact breakdown, use the cost estimation tool in the plan (as seen in the example below):</BodyText>
    {employeeCostsImage}
  </>
);

const answerCoverageStartDate = (
  <>
    <BodyText>Insurance coverage typically begins on the first of the upcoming month.</BodyText>
    <BodyText>
      If it is getting close to the end of the month and you are concerned that your application will not be approved with enough time to start on the first,
      you can confirm the eligibility rules for your specific plan by clicking on "Get Started."
    </BodyText>
    <img
      alt="A card showing a Regence Blue Shield insurance plan
      and premium with a button saying 'Get Started' circled."
      src="/images/faq-images/get_started.png"
    />

    <BodyText>Then select the date that you would like your coverage to begin from the drop-down menu.</BodyText>
    <BodyText>
      In some cases, the carriers will accommodate late applications by either allowing you to backdate coverage to the first or begin coverage mid-month after
      the first of the month. Please check the note displayed after selecting your coverage start date (as seen in the example below).
    </BodyText>
    <img
      alt="A screenshot of a dropdown that allows you to choose
      your coverage start date. 'October 1' is selected. A note
      below the dropdown says, 'Please be sure to complete all
      application steps by October 1.'"
      src="/images/faq-images/select_coverage_start_data.png"
    />
  </>
);

const answerPlanCost = (
  <>
    <BodyText>
      The price that is displayed for a plan is based on many factors but, simply speaking, typically the more expensive the plan, the less you will have to pay
      out-of-pocket for medical expenses. Here are two illustrative examples:
    </BodyText>
    <Ul>
      <Li>
        <strong>Young and Healthy:</strong> your employees are young and do not plan on having regular medical expenses. In this case, they may prefer a lower
        cost per month since they are only in need of coverage for wellness/preventative care and to cover emergencies.
      </Li>
      <Li>
        <strong>Diverse Medical Needs/Expect Surgeries or Pregnancies/Chronic Conditions:</strong> your employees have regular medical needs. In this case, they
        may prefer to pay more per month so they will have to pay less out-of-pocket for each of their medical services.
      </Li>
    </Ul>
    <BodyText>
      To see a detailed breakdown of what each medical service will cost for a given plan, select “View Plan Details”, as seen in the example below:
    </BodyText>
    {planDetailsImage}
  </>
);

const answerProcess = (
  <>
    <BodyText>
      Once you've decided on the medical plan (or plans) that has the right coverage and cost for your business, here's what you can expect when you start an
      application:
    </BodyText>
    <Ul>
      <Li>Add Dental and/or Vision plans</Li>
      <Li>Indicate when you want your coverage to start</Li>
      <Li>Determine what contribution the business will make to the monthly cost and what amount the employees will be expected to pay</Li>
      <Li>Invite your employees to select the plan that is best for them (and their dependents) or to decline the coverage offered</Li>
    </Ul>
    <BodyText>
      The entire application process should take less than 20 minutes to complete and, if you need help along the way, you can contact a licensed broker from
      SimplyInsured by booking a consultation.
    </BodyText>
    <BodyText>
      Once you have provided all of the necessary documentation and all employees have indicated whether they choose to enroll or decline coverage, we will
      submit your information to the carrier, all while being in constant communication about the status of your application.
    </BodyText>
  </>
);

const answerEmployeeEnrollment = (
  <>
    <BodyText>
      As part of the application process, you will be asked to confirm your full-time employees and any other employee(s) to whom you wish to offer health
      benefits.
    </BodyText>
    <BodyText>
      After you have added your employees, you can choose to have us send them an email, which will prompt them to either select a plan or decline coverage. All
      full-time employees must make a selection even if they intend to decline coverage.
    </BodyText>
    <BodyText>
      If your employee(s) do not have access to email or you prefer not to send it to them, there is also an option to complete the enrollment information on
      their behalves.
    </BodyText>
  </>
);

const answerPlanPackages = (
  <>
    <BodyText>You can choose to offer multiple health plan options for your employees via the "Plan Packages" tab.</BodyText>
    <img
      alt="A screenshot from the section below these Frequently Asked
      Questions that shows several tab headers. One of them, called
      'Plan Packages,' is circled."
      src="/images/faq-images/plan_packages.png"
    />

    <BodyText>A few common limitations to be aware of are:</BodyText>
    <Ul>
      <Li>At least one employee must enroll in each plan that is offered</Li>
      <Li>There is a maximum number of plans that can be included in a package (typically 3 or 4, depending on the carrier)</Li>
      <Li>All plans must be offered from the same carrier</Li>
    </Ul>
  </>
);

const answerOwnerBenefits = (
  <>
    <BodyText>Yes, as long as the company is eligible, business owners will be able to receive health benefits.</BodyText>
    <BodyText>When you start an application, we will confirm any plan-specific eligibility requirements for your company.</BodyText>
  </>
);

const answerOutOfState = (
  <>
    <BodyText>
      Yes, but you must select a plan that accommodates out-of-state employees. To view these plans, select the appropriate option for "Out of State Employee
      Coverage" from the "More Filters" tab.
    </BodyText>
    <img
      alt="A dropdown is expanded from a button labeled as 'More Filters.'
      In the dropdown, an option labeled as 'Out of State Employee
      Coverage' is cicled and a checkbox to the left of the label is
      checked."
      src="/images/faq-images/out_of_state.png"
    />
  </>
);

const answerEmployeeCost = (
  <>
    <BodyText>
      The price you see on our quote page is the full monthly premium. If your employee chooses to enroll in the coverage offered, they will only be responsible
      for a portion of the cost and it will be based on the amount you choose to contribute.
    </BodyText>
    <BodyText>If your employee declines coverage, neither the business nor the employee will pay anything.</BodyText>
    <BodyText>To see an exact breakdown, use the cost estimation tool in the plan (as seen in the example below):</BodyText>
    {employeeCostsImage}
  </>
);

const answerPlanPricing = (
  <>
    <BodyText>
      For small businesses, it is required by law that the price of the plan is fixed and determined based on the ages of the employees in the group. It does
      not take into account other factors, such as pre-existing conditions. This is the reason why we are able to display an exact price for each small business
      plan.
    </BodyText>
    <BodyText>All plan pricing is set so there is no difference in price between brokers and there is no negotiating on the price offered.</BodyText>
    <BodyText>
      If your business is or were to grow to a size beyond the "small business" category (50 employees in most states, 100 in a small few) then the prices are
      no longer fixed and could be determined based on other factors. We do support this as well so if your business is in this larger category, please set up a
      consultation.
    </BodyText>
  </>
);

const answerDoctorSearch = (
  <>
    <BodyText>You can search for your doctor using the “Doctor Search” feature.</BodyText>
    <BodyText>First, select View Plan Details.</BodyText>
    {planDetailsImage}
    <BodyText>Then, jump to the Doctor Network.</BodyText>
    <img
      alt="A screenshot of the Plan Details page with a menu on left. In
      the menu, a link that says 'Doctor Network' is circled. On the
      right is a table with a circled row that says 'Doctor Search' and
      has a link with the text 'Tandem PPO Doctor Search.'"
      src="/images/faq-images/doctor_search.png"
    />

    <BodyText>
      If the plan that you have selected does not have an online doctor search, you can contact your doctor directly to ask them if they take the plan you
      intend to select.
    </BodyText>
  </>
);

const answerEmployeeTypes = (
  <>
    <BodyText>
      In most cases, you are required to offer health benefits to full-time employees and you can choose whether or not to also offer to part-time employees and
      contractors.
    </BodyText>
    <BodyText>Once you offer health benefits to your employees and/or contractors, they will have the opportunity to enroll or decline in coverage.</BodyText>
  </>
);

const answerMentalHealth = (
  <>
    <BodyText>To check the coverage for your specific plan, go to View Plan Details. Mental Health Services is located in the Other tab.</BodyText>
    {planDetailsImage}
    <BodyText>Then, select the Other tab</BodyText>
    <img
      alt="A table showing examples of costs for office visits for a
      sample insurance plan. The table has tabs at the top. The tab
      labeled as 'Other' is circled. In the table, a row shows that it
      costs $25 per visit for a mental health appointment."
      src="/images/faq-images/plan_details_other.png"
    />
  </>
);

const answerComparePlans = (
  <>
    <BodyText>You can use the compare feature to see a side-by-side view of the plans’ prices.</BodyText>
    <Ul>
      <Li>Select the checkbox for the plans that you want to compare</Li>
      <Li>Click on the “Compare” button that appears at the bottom of the page</Li>
      <img
        alt="A table showing a list of sample insurance plans.
        Each insurance plan has a checkbox positioned left of the carrier's logo.
        The checkbox for the plans to be compared have been selected and circled.
        At the bottom of the screen, a button titled 'Compare' has been circled. The list plans to be compared
        are positioned right of this button"
        src="/images/faq-images/compare_plans.png"
      />
    </Ul>
  </>
);

export interface FaqEntry {
  id: string;
  question: string;
  answer: React.ReactNode;
}

// Canonical FAQ content. The display lists below select entries by id and attach a position.
export const faqEntries: FaqEntry[] = [
  { id: "employee_cost", question: "How much will this cost my employees?", answer: answerEmployeeCost },
  { id: "eligibility", question: "Is my business qualified to offer health benefits?", answer: answerEligibility },
  { id: "coverage_start", question: "On what date will my insurance begin?", answer: answerCoverageStartDate },
  { id: "monthly_cost", question: "How much will this cost per month?", answer: answerBudgetNoCalculator },
  { id: "employee_enrollment", question: "How do my employees enroll in or decline coverage?", answer: answerEmployeeEnrollment },
  { id: "owner_benefits", question: "Can I receive these benefits as the business owner?", answer: answerOwnerBenefits },
  { id: "plan_cost_variance", question: "Why are some plans more expensive than others?", answer: answerPlanCost },
  { id: "wellness_mental_health", question: "Do plans cover wellness and/or mental health services?", answer: answerMentalHealth },
  { id: "contractors", question: "Can I offer insurance to contractors or only employees on payroll?", answer: answerEmployeeTypes },
  { id: "multiple_plans", question: "How do I offer more than one health plan for my employees to choose from?", answer: answerPlanPackages },
  { id: "application_process", question: "What should I expect after I start an application?", answer: answerProcess },
  { id: "out_of_state", question: "Can I select a plan that will cover employees who are out of state?", answer: answerOutOfState },
  { id: "doctor_search", question: "How do I find out if my doctor takes the plan I’ve selected?", answer: answerDoctorSearch },
  { id: "plan_pricing", question: "How do the carriers determine the pricing of the plans?", answer: answerPlanPricing },
  { id: "compare_plans", question: "How do I compare one plan to another?", answer: answerComparePlans },
];

// Partial: an arbitrary id may not be present, so lookups return FaqEntry | undefined
// and callers must handle the miss.
export const faqEntriesById: Record<string, FaqEntry | undefined> = Object.fromEntries(faqEntries.map((entry) => [entry.id, entry]));

// An entry's place in a display list; question overrides the canonical one when set.
interface FaqPlacement {
  id: string;
  position: number;
  question?: string;
}

const buildList = (placements: FaqPlacement[]) =>
  placements
    .map(({ id, position, question }) => {
      const entry = faqEntriesById[id];
      if (!entry) throw new Error(`FAQText: no entry for id "${id}"`);
      return {
        question: question ?? entry.question,
        answer: entry.answer,
        position,
      };
    })
    .sort((a, b) => a.position - b.position);

export const questionsAndAnswers = () =>
  buildList([
    { id: "application_process", position: 1 },
    { id: "coverage_start", position: 2 },
    { id: "employee_enrollment", position: 3 },
    { id: "eligibility", position: 4 },
    { id: "monthly_cost", position: 5 },
    { id: "out_of_state", position: 6 },
    { id: "owner_benefits", position: 7 },
    { id: "doctor_search", position: 8 },
    { id: "wellness_mental_health", position: 9 },
    { id: "plan_pricing", position: 10 },
    { id: "multiple_plans", position: 11 },
    { id: "contractors", position: 12 },
    { id: "plan_cost_variance", position: 13 },
    { id: "employee_cost", position: 14 },
    { id: "compare_plans", position: 15 },
  ]);

export const simplifiedQuestionsAndAnswers = () =>
  buildList([
    { id: "application_process", position: 1 },
    { id: "coverage_start", position: 2 },
    { id: "employee_enrollment", position: 3 },
    { id: "out_of_state", position: 4, question: "Can I select a plan that will cover out of state employees?" },
    { id: "doctor_search", position: 5 },
    { id: "wellness_mental_health", position: 6 },
    { id: "plan_pricing", position: 7 },
    { id: "multiple_plans", position: 8, question: "How do I offer more than one health plan?" },
    { id: "contractors", position: 9 },
    { id: "plan_cost_variance", position: 10 },
    { id: "employee_cost", position: 11 },
    { id: "compare_plans", position: 12 },
  ]);
